/*
 * A care item reaches the nurse's screen in two shapes.
 *
 * A doctor's order has been through the order sheet, which nests part of the
 * payload under `do_data` and renames the comment to `instructions`. An item
 * the nurse added for an SOS is stored as it was entered, so it keeps the
 * field names the forms use - `notes` for the comment, everything else flat.
 *
 * The cards were written against the first shape only, so an SOS showed
 * blanks where it had perfectly good data. These read either.
 */
const firstValue = (...values) =>
  values.find(
    (value) => value !== undefined && value !== null && value !== ""
  );

/** A field from wherever the record happens to keep it. */
export const careField = (item, name) =>
  firstValue(item?.data?.[name], item?.data?.do_data?.[name]);

/** The comment on the order, under any of the names it is stored by. */
export const careComments = (item) =>
  firstValue(
    item?.data?.instructions,
    item?.data?.notes,
    item?.data?.do_data?.instructions,
    item?.data?.do_data?.notes
  );

/**
 * The label for a choice made on the order - the fluid, the medication, the
 * blood product. `fromDrugName` also looks at `drug_name`, which is where the
 * order sheet keeps whichever choice names the care item.
 */
export const careChoiceLabel = (
  item,
  { name, options, otherName, fromDrugName = false }
) => {
  const value = firstValue(
    careField(item, name),
    fromDrugName ? item?.drug_name : undefined
  );
  if (value === "others") {
    const other = otherName ? careField(item, otherName) : undefined;
    return other ? `Others - ${other}` : "Others";
  }
  return options?.find((option) => option.value === value)?.label || "-";
};

/*
 * A care item the nurse added, under either of the two forms it comes back
 * in: the record of the enable itself, and the record the SOS endpoint
 * creates, which arrives as a care item of its own rather than as an action
 * on the first.
 */
export const nurseCareItemKey = (item) =>
  item?.enabled_care_item_id ??
  (typeof item?.care_id === "string" && item.care_id.startsWith("nurse_sos_")
    ? item.care_id
    : undefined);

export const isNurseAddedCareItem = (item) =>
  nurseCareItemKey(item) !== undefined;

/** Whether a care item has anything charted against it yet. */
export const hasCareCharts = (item, careType) =>
  Boolean(
    (Array.isArray(item?.actions) && item.actions.length) ||
      Object.keys(item || {}).some((key) => key.startsWith(`${careType}_`))
  );
